"use client";

import React, { createContext, useContext, useLayoutEffect, useState } from "react";
import { ThemeProvider as NextThemesProvider, useTheme } from "next-themes";
import { type ThemeProviderProps } from "next-themes/dist/types";

const ForceTheme = createContext<((theme: string | undefined) => void) | null>(
  null
);

export function ThemeModeProvider({
  children,
  forcedTheme,
  ...props
}: ThemeProviderProps) {
  const [forced, setForced] = useState<string>();
  return (
    <ForceTheme.Provider value={setForced}>
      <NextThemesProvider {...props} forcedTheme={forced ?? forcedTheme}>
        {children}
      </NextThemesProvider>
    </ForceTheme.Provider>
  );
}

export const useThemeMode = useTheme;

/**
 * Puts `theme` on <html> for as long as the caller is mounted, without saving
 * it as the viewer's theme, so a section can keep a light/dark choice of its
 * own while the rest of the app keeps the viewer's. One caller at a time: a
 * second one's cleanup would clear the first one's theme.
 *
 * A layout effect, so the forced theme reaches next-themes in the same task as
 * hydration: next-themes applies its saved theme in a passive effect, and a
 * frame painted between the two would flash it.
 */
export function useForcedThemeMode(theme: string | undefined) {
  const force = useContext(ForceTheme);
  useLayoutEffect(() => {
    if (!force) return;
    force(theme);
    return () => force(undefined);
  }, [force, theme]);
}
